
import { Languages } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Button } from './button';
import { 
  SUPPORTED_LANGUAGES, 
  LANGUAGE_NAMES, 
  type SupportedLanguage 
} from '../../lib/language-service';
import { useTranslation } from 'react-i18next';

interface LanguageSelectorProps {
  value?: SupportedLanguage;
  onLanguageChange: (_language: SupportedLanguage) => void;
  disabled?: boolean;
  variant?: 'select' | 'button';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
}

export function LanguageSelector({
  value,
  onLanguageChange,
  disabled = false,
  variant = 'select',
  size = 'md',
  showIcon = true,
  showLabel = false,
}: LanguageSelectorProps) {
  const { t, i18n } = useTranslation();
  
  const currentLanguage = value || (i18n.language as SupportedLanguage) || 'en';

  if (variant === 'button') {
    return (
      <div className="flex items-center gap-2">
        {showLabel && (
          <span className="text-sm font-medium">
            {t('userMenu.language')}
          </span>
        )}
        <div className="flex gap-1" data-testid="language-selector">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <Button
              key={lang}
              variant={currentLanguage === lang ? 'default' : 'outline'}
              size={size === 'md' ? 'default' : size}
              onClick={() => onLanguageChange(lang)}
              disabled={disabled}
              data-testid={`language-button-${lang}`}
              className={`
                ${size === 'sm' ? 'h-8 px-2 text-xs' : 
                  size === 'lg' ? 'h-12 px-4' : 'h-10 px-3'}
                ${currentLanguage === lang ? 'bg-primary text-primary-foreground' : ''}
              `}
            >
              {showIcon && <Languages className="mr-1 h-3 w-3" />}
              {LANGUAGE_NAMES[lang]}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {(showLabel || showIcon) && (
        <span className="text-sm font-medium flex items-center gap-1">
          {showIcon && <Languages className="h-4 w-4" />}
          {showLabel && t('userMenu.language')}
        </span>
      )}
      <Select
        value={currentLanguage}
        onValueChange={(value) => onLanguageChange(value as SupportedLanguage)}
        disabled={disabled}
      >
        <SelectTrigger 
          className={`
            ${size === 'sm' ? 'h-8 text-xs' : 
              size === 'lg' ? 'h-12' : 'h-10'}
            w-auto min-w-[120px]
          `}
          data-testid="language-selector"
        >
          <SelectValue placeholder={t('common.selectLanguage')} />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <SelectItem key={lang} value={lang}>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {LANGUAGE_NAMES[lang]}
                </span>
                {currentLanguage === lang && (
                  <span className="text-xs text-muted-foreground">✓</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}